import { createFileRoute } from "@tanstack/react-router";
import { Layout, CallBanner } from "@/components/site/Layout";
import { Quote } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Bart & The Crew — 40+ Years in Arlington, TX" },
      { name: "description", content: "Bart Baldwin and The Crew bring over 40 years of honest craftsmanship to homeowners across Arlington, Texas." },
      { property: "og:title", content: "About The Crew Paint Contractors" },
      { property: "og:description", content: "Over 40 years of honest craftsmanship in Arlington, TX." },
      { property: "og:url", content: "https://thecrewpainting.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://thecrewpainting.com/about" }],
  }),
  component: About,
});

function About() {
  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-6 pt-20 pb-12 text-center">
        <p className="text-xs uppercase tracking-widest text-accent font-semibold">About</p>
        <h1 className="mt-3 font-display text-4xl md:text-6xl text-primary leading-tight">
          Over 40 Years of Honest Craftsmanship
        </h1>
        <p className="mt-6 text-lg text-muted-foreground">
          Owned and operated by Bart Baldwin in Arlington, Texas.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-6 mt-8 space-y-6 text-lg text-foreground/85 leading-relaxed">
        <h2 className="font-display text-3xl text-primary">Our story</h2>
        <p>
          Bart Baldwin started painting houses in 1984. Four decades later, he's still doing it — the same way, with the same standards, for homeowners across Arlington and the surrounding communities.
        </p>
        <p>
          The Crew was built on a simple idea: solve real home maintenance and appearance problems with experience, honesty, and quality work. No flashy sales pitch. No revolving door of subcontractors. Just a small, dedicated team that treats every home like it's their own.
        </p>
        <p>
          That tenure matters. Forty years means we've seen how Texas weather punishes paint and wood. We know which products hold up, which prep work pays off, and which shortcuts cost homeowners thousands down the road. We don't take those shortcuts.
        </p>
      </section>

      {/* Commitment */}
      <section className="mx-auto max-w-7xl px-6 mt-20">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 relative overflow-hidden">
          <Quote className="absolute top-8 right-8 h-24 w-24 text-accent/15" />
          <h2 className="text-xs uppercase tracking-widest text-accent font-semibold">The Commitment</h2>
          <p className="mt-4 font-display text-2xl md:text-4xl text-primary leading-snug max-w-3xl">
            "We don't leave you hanging. We show up every single day and we communicate clearly from start to finish."
          </p>
          <p className="mt-6 text-sm text-muted-foreground">— Bart Baldwin, Owner</p>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-7xl px-6 mt-20">
        <h2 className="sr-only">The Crew by the numbers</h2>
        <div className="grid md:grid-cols-3 gap-6">
        {[
          { n: "40+", l: "Years in business" },
          { n: "1984", l: "Year founded" },
          { n: "Arlington", l: "Proudly serving TX" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border border-border bg-card p-8 text-center">
            <p className="font-display text-4xl md:text-5xl text-primary">{s.n}</p>
            <p className="mt-2 text-sm uppercase tracking-widest text-muted-foreground">{s.l}</p>
          </div>
        ))}
        </div>
      </section>

      <CallBanner text="Talk to Bart directly. No gatekeepers, no sales reps — just the owner." />
    </Layout>
  );
}
